import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { waLink, smsLink, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", pickup: "", destination: "", datetime: "", passengers: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-stone-700 bg-[#2C251F] px-4 py-3.5 text-[15px] text-stone-100 placeholder:text-stone-500 transition-shadow duration-300";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-stone-400 mb-2">{label}</span>
    {children}
  </label>
);

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const { lang } = useLang();
  const t = T[lang].booking;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildMessage = () =>
    [
      t.wa.header,
      `${t.wa.name}: ${form.name}`,
      `${t.wa.phone}: ${form.phone}`,
      form.email && `E-Mail: ${form.email}`,
      `${t.wa.pickup}: ${form.pickup}`,
      `${t.wa.destination}: ${form.destination}`,
      form.datetime && `${t.wa.datetime}: ${form.datetime}`,
      form.passengers && `${t.wa.passengers}: ${form.passengers}`,
      form.message && `${t.wa.message}: ${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success(t.toastSuccess);
      setForm(initial);
    } catch {
      toast.error(t.toastError);
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = () => {
    if (!form.name || !form.pickup) {
      toast.info(t.toastInfo);
      return;
    }
    window.open(waLink(buildMessage()), "_blank", "noopener,noreferrer");
  };

  const sendSMS = () => {
    if (!form.name || !form.pickup) {
      toast.info(t.toastInfo);
      return;
    }
    window.location.href = smsLink(buildMessage());
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 lg:py-36 bg-[#201B16] border-y border-[#C5A059]/20 overflow-hidden">
      <img
        src="/images/booking-bg.webp"
        alt=""
        aria-hidden="true"
        loading="lazy" decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#16110C]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#201B16] via-[#201B16]/35 to-[#201B16]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-[1fr_1.15fr] gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="rounded-3xl bg-[#201B16]/75 backdrop-blur border border-[#C5A059]/25 p-8 lg:p-10 shadow-xl shadow-black/40"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>
          </h2>
          <p className="mt-6 text-stone-400 leading-relaxed max-w-md">{t.p}</p>
        </motion.div>

        <motion.form
          data-testid="booking-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="rounded-3xl bg-[#342A18] border border-[#C5A059]/30 shadow-xl shadow-black/40 p-7 lg:p-10 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t.labels.name} testid="booking-field-name">
              <input data-testid="booking-input-name" required value={form.name} onChange={set("name")} placeholder={t.placeholders.name} className={inputCls} />
            </Field>
            <Field label={t.labels.phone} testid="booking-field-phone">
              <input data-testid="booking-input-phone" required value={form.phone} onChange={set("phone")} placeholder={t.placeholders.phone} className={inputCls} />
            </Field>
          </div>
          <Field label={t.labels.email} testid="booking-field-email">
            <input data-testid="booking-input-email" type="email" value={form.email} onChange={set("email")} placeholder={t.placeholders.email} className={inputCls} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t.labels.pickup} testid="booking-field-pickup">
              <input data-testid="booking-input-pickup" required value={form.pickup} onChange={set("pickup")} placeholder={t.placeholders.pickup} className={inputCls} />
            </Field>
            <Field label={t.labels.destination} testid="booking-field-destination">
              <input data-testid="booking-input-destination" required value={form.destination} onChange={set("destination")} placeholder={t.placeholders.destination} className={inputCls} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label={t.labels.datetime} testid="booking-field-datetime">
              <input data-testid="booking-input-datetime" type="datetime-local" value={form.datetime} onChange={set("datetime")} className={inputCls} />
            </Field>
            <Field label={t.labels.passengers} testid="booking-field-passengers">
              <select data-testid="booking-input-passengers" value={form.passengers} onChange={set("passengers")} className={inputCls}>
                <option value="">{t.select}</option>
                {["1", "2", "3", "4", "5", "6", "7", "8"].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label={t.labels.message} testid="booking-field-message">
            <textarea data-testid="booking-input-message" rows={3} value={form.message} onChange={set("message")} placeholder={t.placeholders.message} className={`${inputCls} resize-none`} />
          </Field>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#C5A059] text-[#141312] px-6 py-4 text-sm font-semibold tracking-wide hover:bg-[#D9B877] transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t.submit}
            </button>
            <button
              data-testid="booking-whatsapp-button"
              type="button"
              onClick={sendWhatsApp}
              className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-4 text-sm font-semibold tracking-wide hover:bg-[#1DA851] transition-colors duration-300"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t.whatsapp}
            </button>
          </div>
          <button
            data-testid="booking-sms-button"
            type="button"
            onClick={sendSMS}
            className="w-full flex items-center justify-center gap-2 rounded-full border border-stone-600 text-stone-300 px-6 py-3.5 text-sm font-semibold tracking-wide hover:border-[#C5A059]/60 hover:text-[#C5A059] transition-colors duration-300"
          >
            <MessageSquare className="w-4 h-4" />
            {t.sms}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
